import { useState, useRef, useEffect } from 'react';
import { getVideoAnalytics, VIDEO_ANALYTICS_KEY, type VideoAnalytics } from '@/components/feature/VideoPopup';

export const VIDEO_POPUP_KEY = 'admin_video_popup';

export type VideoPopupCorner = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

export interface VideoPopupConfig {
  enabled: boolean;
  videoUrl: string;
  productLink: string;
  productId: string | null;
  title: string;
  popupWidth: number;
  corner: VideoPopupCorner;
  showOncePerSession: boolean;
  updatedAt: number;
}

export function getVideoPopupConfig(): VideoPopupConfig | null {
  try {
    const saved = localStorage.getItem(VIDEO_POPUP_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

interface Props {
  onSave?: () => void;
}

export default function VideoPopupPanel({ onSave }: Props) {
  const [config, setConfig] = useState<VideoPopupConfig>(() => {
    const saved = getVideoPopupConfig();
    return saved ?? {
      enabled: false,
      videoUrl: '',
      productLink: '',
      productId: null,
      title: '',
      popupWidth: 160,
      corner: 'bottom-left' as VideoPopupCorner,
      showOncePerSession: true,
      updatedAt: 0,
    };
  });

  const [saved, setSaved] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [analytics, setAnalytics] = useState<VideoAnalytics>(() => getVideoAnalytics());
  const [analyticsTab, setAnalyticsTab] = useState<'overview' | 'daily'>('overview');

  // Load listed products for product link dropdown
  const [products, setProducts] = useState<{ id: number; name: string; price: number }[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('listed_products');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setProducts(parsed.map((p: { id: number; name: string; price: number }) => ({ id: p.id, name: p.name, price: p.price })));
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Refresh analytics every time panel is opened
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === VIDEO_ANALYTICS_KEY) {
        setAnalytics(getVideoAnalytics());
      }
    };
    window.addEventListener('storage', onStorage);
    // Also refresh on mount
    setAnalytics(getVideoAnalytics());
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleResetAnalytics = () => {
    const fresh: VideoAnalytics = { totalViews: 0, totalClicks: 0, lastUpdated: Date.now(), dailyStats: [] };
    localStorage.setItem(VIDEO_ANALYTICS_KEY, JSON.stringify(fresh));
    setAnalytics(fresh);
  };

  const ctr = analytics.totalViews > 0
    ? ((analytics.totalClicks / analytics.totalViews) * 100).toFixed(1)
    : '0.0';

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setUploadError('Sirf video file upload karo (MP4, MOV, etc.)');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadError('Video size 50MB se zyada nahi honi chahiye');
      return;
    }

    setUploadError('');
    setVideoUploading(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setConfig(prev => ({ ...prev, videoUrl: base64 }));
      setVideoUploading(false);
    };
    reader.onerror = () => {
      setVideoUploading(false);
      setUploadError('Video upload failed. Dobara try karo.');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    const toSave: VideoPopupConfig = { ...config, updatedAt: Date.now() };
    localStorage.setItem(VIDEO_POPUP_KEY, JSON.stringify(toSave));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    onSave?.();
  };

  const handleToggleShowOnce = () => {
    const updated = { ...config, showOncePerSession: !config.showOncePerSession, updatedAt: Date.now() };
    setConfig(updated);
    localStorage.setItem(VIDEO_POPUP_KEY, JSON.stringify(updated));
  };

  const handleToggle = (val: boolean) => {
    const updated = { ...config, enabled: val, updatedAt: Date.now() };
    setConfig(updated);
    localStorage.setItem(VIDEO_POPUP_KEY, JSON.stringify(updated));
  };

  const handleProductSelect = (productId: string) => {
    const product = products.find(p => String(p.id) === productId);
    setConfig(prev => ({
      ...prev,
      productId: productId || null,
      productLink: productId ? `/category?product=${productId}` : '',
      title: product ? product.name : prev.title,
    }));
  };

  const handleRemoveVideo = () => {
    setConfig(prev => ({ ...prev, videoUrl: '' }));
  };

  return (
    <div className="space-y-5">
      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
            <i className="ri-video-line text-rose-600 text-lg"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-base">Video Popup Manager</p>
            <p className="text-gray-400 text-xs">Website open hone ke 5 second baad customer ko video dikhao</p>
          </div>
        </div>

        {/* Enable Toggle */}
        <div className={`rounded-2xl border p-4 transition-all mb-4 ${config.enabled ? 'bg-rose-50 border-rose-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.enabled ? 'bg-rose-500' : 'bg-gray-300'}`}>
                <i className="ri-play-circle-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">Video Popup</p>
                <p className={`text-xs ${config.enabled ? 'text-rose-600' : 'text-gray-400'}`}>
                  {config.enabled ? 'Active — customers ko dikhega' : 'Disabled — customers ko nahi dikhega'}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleToggle(!config.enabled)}
              className={`relative w-12 h-6 rounded-full transition-all cursor-pointer shrink-0 ${config.enabled ? 'bg-rose-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${config.enabled ? 'translate-x-6' : 'translate-x-0'}`}></span>
            </button>
          </div>
        </div>

        {/* Show Again Toggle - Har Refresh Pe Dikhao */}
        <div className="rounded-2xl border border-gray-200 p-4 transition-all mb-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${!config.showOncePerSession ? 'bg-green-500' : 'bg-amber-400'}`}>
                <i className={`${!config.showOncePerSession ? 'ri-repeat-line' : 'ri-repeat-one-line'} text-white text-lg`}></i>
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">Show Again (Har Refresh Pe)?</p>
                <p className="text-xs text-gray-500">
                  {!config.showOncePerSession ? 'ON — Har refresh pe video dikhega' : 'OFF — Sirf ek baar dikhega'}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleShowOnce}
              className={`relative w-12 h-6 rounded-full transition-all cursor-pointer shrink-0 ${!config.showOncePerSession ? 'bg-green-500' : 'bg-amber-400'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${!config.showOncePerSession ? 'translate-x-6' : 'translate-x-0'}`}></span>
            </button>
          </div>
          
          {/* Info Box */}
          <div className={`mt-3 rounded-xl p-3 flex items-start gap-2 ${!config.showOncePerSession ? 'bg-green-100 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
            <i className={`${!config.showOncePerSession ? 'ri-check-line text-green-600' : 'ri-information-line text-amber-500'} shrink-0 mt-0.5`}></i>
            <p className={`text-xs ${!config.showOncePerSession ? 'text-green-700' : 'text-amber-700'}`}>
              {!config.showOncePerSession 
                ? 'Video har refresh pe dikhega — customer kitni baar bhi page reload kare, video aayega' 
                : 'Video sirf ek hi baar dikhega — customer ne ek baar dekh liya toh wapas nahi aayega jab tak browser band na kare'}
            </p>
          </div>
          
          {/* Reset Session Button */}
          <button
            onClick={() => {
              sessionStorage.removeItem('video_popup_shown');
              alert('Session reset ho gaya! Ab refresh karo, video dikhega.');
            }}
            className="mt-3 w-full py-2 rounded-lg bg-gray-200 text-gray-700 text-xs font-bold flex items-center justify-center gap-2 hover:bg-gray-300 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Test Karne Ke Liye Session Reset Karo
          </button>
          {/* Force Show Button */}
          <button
            onClick={() => {
              // Open website in new tab with force show param
              const baseUrl = window.location.origin;
              window.open(`${baseUrl}/?force_video_popup=1`, '_blank');
            }}
            className="mt-2 w-full py-2 rounded-lg bg-rose-100 text-rose-700 text-xs font-bold flex items-center justify-center gap-2 hover:bg-rose-200 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line"></i>
            Website Open Karo (Test Mode)
          </button>
        </div>

        {/* Video Upload */}
        <div className="mb-4">
          <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1">
            <i className="ri-upload-cloud-line text-rose-500"></i>
            Video Upload
          </label>

          {config.videoUrl ? (
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden border border-gray-200 bg-black" style={{ height: '200px' }}>
                <video
                  src={config.videoUrl}
                  controls
                  className="w-full h-full object-contain"
                  preload="metadata"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={videoUploading}
                  className="flex-1 py-2.5 rounded-xl bg-gray-900 text-white text-xs font-bold flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <i className="ri-refresh-line"></i>
                  Video Change Karo
                </button>
                <button
                  onClick={handleRemoveVideo}
                  className="px-4 py-2.5 rounded-xl bg-red-50 text-red-600 text-xs font-bold flex items-center gap-2 hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line"></i>
                  Hatao
                </button>
                <button
                  onClick={() => setPreviewOpen(true)}
                  className="px-4 py-2.5 rounded-xl bg-rose-50 text-rose-600 text-xs font-bold flex items-center gap-2 hover:bg-rose-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-eye-line"></i>
                  Preview
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => !videoUploading && fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-rose-400 hover:bg-rose-50/50 transition-all"
            >
              {videoUploading ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full border-4 border-rose-200 border-t-rose-500 animate-spin"></div>
                  <p className="text-rose-600 text-sm font-semibold">Video upload ho raha hai...</p>
                </div>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-video-add-line text-2xl text-rose-500"></i>
                  </div>
                  <p className="text-gray-700 font-semibold text-sm mb-1">Video Upload Karo</p>
                  <p className="text-gray-400 text-xs">MP4, MOV · Max 50MB</p>
                  <p className="text-rose-500 text-xs mt-2 font-medium">Tap to select video</p>
                </>
              )}
            </div>
          )}

          {uploadError && (
            <div className="mt-2 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 shrink-0 mt-0.5"></i>
              <p className="text-red-600 text-xs">{uploadError}</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="hidden"
          />
        </div>

        {/* Video Title */}
        <div className="mb-4">
          <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1">
            <i className="ri-text text-gray-500"></i>
            Video Title (Optional)
          </label>
          <input
            type="text"
            value={config.title}
            onChange={e => setConfig(prev => ({ ...prev, title: e.target.value }))}
            placeholder="e.g. New Collection Dekho! 🔥"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none"
          />
          <p className="text-gray-400 text-xs mt-1">Popup ke upar dikhega (agar khaali rakho toh nahi dikhega)</p>
        </div>

        {/* Popup Size — Arrow Control */}
        <div className="mb-4">
          <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1">
            <i className="ri-aspect-ratio-line text-rose-500"></i>
            Popup Size (Width)
          </label>
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
            <button
              onClick={() => setConfig(prev => ({ ...prev, popupWidth: Math.max(80, (prev.popupWidth ?? 160) - 10) }))}
              className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-100 active:scale-95 transition-all cursor-pointer shrink-0"
            >
              <i className="ri-subtract-line text-base font-bold"></i>
            </button>
            <div className="flex-1 text-center">
              <p className="text-2xl font-black text-gray-900">{config.popupWidth ?? 160}<span className="text-sm font-semibold text-gray-400 ml-0.5">px</span></p>
              <p className="text-[10px] text-gray-400 mt-0.5">Min 80px · Max 400px</p>
            </div>
            <button
              onClick={() => setConfig(prev => ({ ...prev, popupWidth: Math.min(400, (prev.popupWidth ?? 160) + 10) }))}
              className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-100 active:scale-95 transition-all cursor-pointer shrink-0"
            >
              <i className="ri-add-line text-base font-bold"></i>
            </button>
          </div>
          <div className="flex justify-between mt-1.5 px-1">
            {[80, 120, 160, 200, 280, 360, 400].map(w => (
              <button
                key={w}
                onClick={() => setConfig(prev => ({ ...prev, popupWidth: w }))}
                className={`text-[9px] font-bold px-1.5 py-0.5 rounded transition-all cursor-pointer whitespace-nowrap ${
                  (config.popupWidth ?? 160) === w ? 'bg-rose-500 text-white' : 'text-gray-400 hover:text-gray-600'
                }`}
              >{w}</button>
            ))}
          </div>
        </div>

        {/* Corner Position Selector */}
        <div className="mb-4">
          <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1">
            <i className="ri-layout-4-line text-rose-500"></i>
            Popup Position (Corner)
          </label>
          {/* Visual 2×2 grid representing screen corners */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <p className="text-[10px] text-gray-400 text-center mb-2">Screen ka corner select karo</p>
            <div className="relative bg-white border-2 border-gray-200 rounded-xl overflow-hidden mx-auto" style={{ height: '100px', maxWidth: '200px' }}>
              {/* Screen mock lines */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-px h-full bg-gray-100 absolute left-1/2"></div>
                <div className="h-px w-full bg-gray-100 absolute top-1/2"></div>
              </div>
              {/* 4 corner buttons */}
              {([
                { id: 'top-left', label: 'TL', pos: 'top-1 left-1' },
                { id: 'top-right', label: 'TR', pos: 'top-1 right-1' },
                { id: 'bottom-left', label: 'BL', pos: 'bottom-1 left-1' },
                { id: 'bottom-right', label: 'BR', pos: 'bottom-1 right-1' },
              ] as { id: VideoPopupCorner; label: string; pos: string }[]).map(c => (
                <button
                  key={c.id}
                  onClick={() => setConfig(prev => ({ ...prev, corner: c.id }))}
                  className={`absolute ${c.pos} w-9 h-9 rounded-lg text-[10px] font-black transition-all cursor-pointer flex items-center justify-center ${
                    (config.corner ?? 'bottom-left') === c.id
                      ? 'bg-rose-500 text-white shadow-lg scale-110'
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                >
                  <i className="ri-vidicon-fill text-sm"></i>
                </button>
              ))}
            </div>
            <p className="text-center text-xs font-bold text-gray-700 mt-2">
              {(config.corner ?? 'bottom-left') === 'bottom-left' && 'Neeche Left'}
              {(config.corner ?? 'bottom-left') === 'bottom-right' && 'Neeche Right'}
              {(config.corner ?? 'bottom-left') === 'top-left' && 'Upar Left'}
              {(config.corner ?? 'bottom-left') === 'top-right' && 'Upar Right'}
            </p>
          </div>
        </div>

        {/* Product Link */}
        <div className="mb-5">
          <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1">
            <i className="ri-links-line text-rose-500"></i>
            Video Pe Click Karne Pe Kahan Jaaye?
          </label>

          {products.length > 0 ? (
            <div className="space-y-3">
              <select
                value={config.productId ?? ''}
                onChange={e => handleProductSelect(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-rose-400 outline-none bg-white"
              >
                <option value="">-- Product select karo --</option>
                {products.map(p => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name} — ₹{p.price}
                  </option>
                ))}
              </select>

              {config.productLink && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500"></i>
                  <div>
                    <p className="text-green-700 text-xs font-semibold">Product linked!</p>
                    <p className="text-green-600 text-xs font-mono">{config.productLink}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
              <i className="ri-information-line text-amber-500 shrink-0 mt-0.5"></i>
              <div>
                <p className="text-amber-800 text-xs font-semibold">Koi product nahi mila</p>
                <p className="text-amber-700 text-xs mt-0.5">Pehle Products tab mein product add karo, phir yahan link kar sakte ho</p>
              </div>
            </div>
          )}

          <p className="text-gray-400 text-xs mt-2">
            <i className="ri-cursor-line mr-1"></i>
            Customer video pe click karega toh is product ke page par jayega
          </p>
        </div>

        {/* Save Button */}
        {saved ? (
          <div className="w-full py-3 rounded-xl bg-green-100 border border-green-300 text-green-700 font-bold text-sm flex items-center justify-center gap-2">
            <i className="ri-checkbox-circle-fill text-green-500"></i>
            Video Popup Settings Saved!
          </div>
        ) : (
          <button
            onClick={handleSave}
            disabled={!config.videoUrl && config.enabled}
            className="w-full py-3.5 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-save-line"></i>
            Save Video Popup Settings
          </button>
        )}

        {!config.videoUrl && config.enabled && (
          <p className="text-red-500 text-xs text-center mt-2 flex items-center justify-center gap-1">
            <i className="ri-error-warning-line"></i>
            Pehle video upload karo
          </p>
        )}
      </div>

      {/* Analytics Section */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <i className="ri-bar-chart-2-line text-violet-600 text-lg"></i>
            </div>
            <div>
              <p className="text-gray-900 font-bold text-base">Video Analytics</p>
              <p className="text-gray-400 text-xs">Video views aur product clicks ka data</p>
            </div>
          </div>
          <button
            onClick={handleResetAnalytics}
            className="px-3 py-1.5 rounded-lg bg-red-50 text-red-500 text-xs font-semibold flex items-center gap-1.5 hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>
            Reset
          </button>
        </div>

        {/* Tab Switch */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-4 gap-1">
          <button
            onClick={() => setAnalyticsTab('overview')}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${analyticsTab === 'overview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setAnalyticsTab('daily')}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${analyticsTab === 'daily' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Daily Stats
          </button>
        </div>

        {analyticsTab === 'overview' ? (
          <div className="space-y-3">
            {/* Stat Cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-violet-50 border border-violet-100 rounded-2xl p-3 text-center">
                <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-eye-line text-violet-600 text-base"></i>
                </div>
                <p className="text-2xl font-black text-violet-700 leading-none">{analytics.totalViews}</p>
                <p className="text-violet-500 text-[10px] font-semibold mt-1 uppercase tracking-wide">Total Views</p>
              </div>
              <div className="bg-rose-50 border border-rose-100 rounded-2xl p-3 text-center">
                <div className="w-9 h-9 rounded-xl bg-rose-100 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-cursor-line text-rose-600 text-base"></i>
                </div>
                <p className="text-2xl font-black text-rose-700 leading-none">{analytics.totalClicks}</p>
                <p className="text-rose-500 text-[10px] font-semibold mt-1 uppercase tracking-wide">Clicks</p>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-2xl p-3 text-center">
                <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-percent-line text-green-600 text-base"></i>
                </div>
                <p className="text-2xl font-black text-green-700 leading-none">{ctr}%</p>
                <p className="text-green-500 text-[10px] font-semibold mt-1 uppercase tracking-wide">CTR</p>
              </div>
            </div>

            {/* CTR Progress Bar */}
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                  <i className="ri-bar-chart-grouped-line text-violet-500"></i>
                  Click-Through Rate
                </p>
                <span className="text-xs font-black text-gray-900">{ctr}%</span>
              </div>
              <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-rose-500 rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(parseFloat(ctr), 100)}%` }}
                />
              </div>
              <p className="text-gray-400 text-[10px] mt-1.5">
                {analytics.totalViews} logo ne video dekha → {analytics.totalClicks} ne product pe click kiya
              </p>
            </div>

            {analytics.totalViews === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-information-line text-amber-500 shrink-0 mt-0.5"></i>
                <p className="text-amber-700 text-xs">Abhi tak koi data nahi. Video popup enable karo, customers ke views aane ke baad yahan data dikhega.</p>
              </div>
            )}
          </div>
        ) : (
          <div>
            {analytics.dailyStats.length === 0 ? (
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-calendar-line text-gray-400 text-xl"></i>
                </div>
                <p className="text-gray-500 text-sm font-semibold">Koi daily data nahi</p>
                <p className="text-gray-400 text-xs mt-1">Jab customers video dekhenge tab yahan daily breakdown dikhega</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {[...analytics.dailyStats].reverse().map((day) => {
                  const dayCtr = day.views > 0 ? ((day.clicks / day.views) * 100).toFixed(0) : '0';
                  const dateObj = new Date(day.date);
                  const dateLabel = dateObj.toLocaleDateString('hi-IN', { day: 'numeric', month: 'short' });
                  return (
                    <div key={day.date} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
                      <div className="w-12 text-center">
                        <p className="text-xs font-bold text-gray-700">{dateLabel}</p>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-1 mb-1">
                          <div className="h-1.5 bg-violet-300 rounded-full" style={{ width: `${Math.min(day.views * 3, 80)}px` }}></div>
                          <span className="text-[10px] text-violet-600 font-bold">{day.views} views</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="h-1.5 bg-rose-300 rounded-full" style={{ width: `${Math.min(day.clicks * 3, 80)}px` }}></div>
                          <span className="text-[10px] text-rose-600 font-bold">{day.clicks} clicks</span>
                        </div>
                      </div>
                      <div className="w-12 text-right">
                        <span className="text-xs font-black text-green-600">{dayCtr}%</span>
                        <p className="text-[9px] text-gray-400">CTR</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {analytics.lastUpdated > 0 && analytics.totalViews > 0 && (
          <p className="text-gray-400 text-[10px] text-right mt-3 flex items-center justify-end gap-1">
            <i className="ri-time-line"></i>
            Last updated: {new Date(analytics.lastUpdated).toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>

      {/* How it Works */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <p className="text-gray-700 font-bold text-sm mb-3 flex items-center gap-2">
          <i className="ri-lightbulb-flash-line text-amber-500"></i>
          Yeh Feature Kaise Kaam Karta Hai
        </p>
        <ul className="space-y-2 text-gray-500 text-xs">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
            <span>Video upload karo aur product link select karo</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
            <span>Enable toggle ON karo aur Save karo</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
            <span>Customer jab bhi website kholega, <strong>3 seconds baad</strong> video popup dikhega</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">4</span>
            <span><strong>Video pe click</strong> karne se linked product page par jayega</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-600 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">5</span>
            <span><strong>Ek Baar Dikhao</strong> toggle ON = ek session mein ek baar dikhega (refresh pe nahi)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">6</span>
            <span>Side mein <strong>X button</strong> dabake popup band kar sakta hai</span>
          </li>
        </ul>
      </div>

      {/* Preview Modal — no dark bg, bottom-left, square video */}
      {previewOpen && config.videoUrl && (
        <div className="fixed inset-0 z-[70] pointer-events-none">
          {/* Close button — top center, always visible */}
          <div className="pointer-events-auto absolute top-4 left-1/2 -translate-x-1/2">
            <button
              onClick={() => setPreviewOpen(false)}
              className="flex items-center gap-2 bg-gray-900 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg hover:bg-gray-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-sm"></i>
              Close Preview
            </button>
          </div>
          {/* Popup — selected corner */}
          <div
            className={`pointer-events-auto absolute ${
              (config.corner ?? 'bottom-left') === 'bottom-left' ? 'bottom-6 left-4' :
              (config.corner ?? 'bottom-left') === 'bottom-right' ? 'bottom-6 right-4' :
              (config.corner ?? 'bottom-left') === 'top-left' ? 'top-16 left-4' :
              'top-16 right-4'
            }`}
            style={{ width: `${config.popupWidth ?? 160}px`, height: `${config.popupWidth ?? 160}px` }}
          >
            <div
              className="w-full h-full rounded-xl overflow-hidden shadow-2xl border border-gray-200 bg-black cursor-pointer relative"
            >
              <video
                src={config.videoUrl}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
                onLoadedMetadata={(e) => {
                  // Force play after metadata loaded (browser autoplay policy workaround)
                  const video = e.currentTarget;
                  video.play().catch(() => {
                    // Silent fail - user interaction needed
                  });
                }}
              />
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-gray-900 rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:bg-gray-700 transition-all"
              onClick={() => setPreviewOpen(false)}>
              <i className="ri-close-line text-white text-xs"></i>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
